import type { components as internalComponents } from '@/generated/internal-api/openapi-types';
import type { components as messagingComponents } from '@/generated/messaging-api/openapi-types';

export type AgentType = internalComponents['schemas']['AgentType.schema'];
export type AgentSessionApiId = internalComponents['schemas']['AgentSessionApiId.schema'];
export type AgentSessionStatus = internalComponents['schemas']['AgentSessionStatus.schema'];
export type AgentDefinitionV1 = internalComponents['schemas']['AgentDefinitionV1.schema'];
export type AgentSessionSummaryV1 = internalComponents['schemas']['AgentSessionSummaryV1.schema'];
export type AgentSessionV1 = internalComponents['schemas']['AgentSessionV1.schema'];
export type AgentFindingCountsV1 = internalComponents['schemas']['AgentFindingCountsV1.schema'];
export type StudyHealthCheckSessionSummaryV1 = internalComponents['schemas']['StudyHealthCheckSessionSummaryV1.schema'];
export type ProtocolFormat = internalComponents['schemas']['ProtocolFormat.schema'];
export type SendValidatorDomainConfirmationV1 =
  internalComponents['schemas']['SendValidatorDomainConfirmationV1.schema'];

/**
 * A report node's scorecard. Generated from the schema rather than hand-written, so the types cannot
 * drift from the server.
 */
export type AgentReportV1 = internalComponents['schemas']['AgentReportV1.schema'];
export type ScorecardFindingV1 = AgentReportV1['findings'][number];
export type ScorecardSeverity = ScorecardFindingV1['severity'];

export type AgentTreeV1 = internalComponents['schemas']['AgentTreeV1.schema'];
export type AgentTreeNodeV1 = internalComponents['schemas']['AgentTreeNodeV1.schema'];
export type AgentTreeSessionV1 = internalComponents['schemas']['AgentTreeSessionV1.schema'];
export type AgentInputV1 = internalComponents['schemas']['AgentInputV1.schema'];
export type AgentTreeNodeApiId = internalComponents['schemas']['AgentTreeNodeApiId.schema'];
export type AgentTreeNodeType = internalComponents['schemas']['AgentTreeNodeType.schema'];

/** A link node's destination as a typed target, not a URL. Present only on a link node. */
export type AgentTreeNodeLinkV1 = internalComponents['schemas']['AgentTreeNodeLinkV1.schema'];
export type AgentExecutionApiId = internalComponents['schemas']['AgentExecutionApiId.schema'];
export type AgentAttachmentApiId = internalComponents['schemas']['AgentAttachmentApiId.schema'];
export type AgentAttachmentBatchApiId = internalComponents['schemas']['AgentAttachmentBatchApiId.schema'];

/**
 * One entry of a session's durable stream-event history (`GET .../events`): the audit-row id (a
 * stable React key), the recomposed Redis stream id (`<ms>-<seq>`) when the event reached the live
 * stream else null, and the deserialised {@link AgentStreamEventV1} payload.
 */
export type AgentExecutionEventV1 = internalComponents['schemas']['AgentExecutionEventV1.schema'];

type GeneratedAgentStreamEventV1 = messagingComponents['schemas']['AgentStreamEventV1.schema'];

export type AgentStreamEventType = GeneratedAgentStreamEventV1['type'];

/**
 * The values a run can terminate on. Each is BOTH a stream event type and a session status (the
 * terminal event type IS the resulting status), so the values satisfy the intersection — usable to
 * subscribe to the stream event AND to set the session status without a cast. Single source for the
 * two terminal Sets below.
 */
export const TerminalEventStatus = {
  Completed: 'completed',
  Failed: 'failed',
  Cancelled: 'cancelled',
} as const satisfies Record<string, AgentStreamEventType & AgentSessionStatus>;
export type TerminalEventStatus = (typeof TerminalEventStatus)[keyof typeof TerminalEventStatus];

/** Stream event types that close the stream (no further events expected). */
export const TERMINAL_STREAM_EVENTS: ReadonlySet<AgentStreamEventType> = new Set(Object.values(TerminalEventStatus));

/** Session statuses that mean a run has finished (no further events expected). */
export const TERMINAL_SESSION_STATUSES: ReadonlySet<AgentSessionStatus> = new Set(Object.values(TerminalEventStatus));

type WithId<T> = T extends unknown ? T & { id?: string } : never;

/**
 * Stream event delivered over SSE. The optional `id` is injected by AgentSessionStreamService
 * from the SSE lastEventId so the consumer can deduplicate replays on reconnect.
 */
export type AgentStreamEventV1 = WithId<GeneratedAgentStreamEventV1>;

/**
 * One numeric fact carried on a `phase_transition` — the "120 animals" behind a "Loaded animals"
 * Activity row. `key` is a server identifier looked up in the UI's copy table, never display text.
 */
export type AgentActivityMetric = messagingComponents['schemas']['AgentActivityMetric.schema'];

/**
 * The closed vocabularies the server emits on phase and subagent events. Append-only, so a value
 * this build does not recognise arrives as `unknown` rather than breaking the feed.
 */
export type AgentActivityPhase = messagingComponents['schemas']['AgentActivityPhase.schema'];
export type AgentSubagentName = messagingComponents['schemas']['AgentSubagentName.schema'];

export type AgentSessionPhase = 'idle' | 'creating' | 'streaming' | 'completed' | 'failed' | 'cancelled';

/**
 * The SEND Validator's execution results, as the server publishes them. Aliases only: the types are
 * generated from the server's schemas, which a contract test holds to the records that write them.
 * Change the server schema, not these. Tell the results apart by `result_type` (see `sendResults.ts`),
 * never by which fields a payload happens to carry.
 */
export type SendExecutionResult = internalComponents['schemas']['SendExecutionResult.schema'];
export type SendResultType = SendExecutionResult['result_type'];
export type SendBundleValidationResult = internalComponents['schemas']['SendBundleValidationResult.schema'];
export type SendBundleDetectionResult = internalComponents['schemas']['SendBundleDetectionResult.schema'];
export type SendInputFailureResult = internalComponents['schemas']['SendInputFailureResult.schema'];
export type SendInputFailureKind = internalComponents['schemas']['SendInputFailureKind.schema'];
export type SendInputFailure = internalComponents['schemas']['SendInputFailure.schema'];
/** One file's detected domain and candidates, as the confirm step lists it. */
export type SendFileDetection = internalComponents['schemas']['SendFileDetection.schema'];
/**
 * A column that looks rewritten by a spreadsheet. A warning, never a failure: the run goes ahead. Absent
 * from a detection stored before files were scanned, which is not the same as a clean scan.
 */
export type SendCoercionWarning = internalComponents['schemas']['SendCoercionWarning.schema'];
export type SendCoercionWarningKind = internalComponents['schemas']['SendCoercionWarningKind.schema'];

/** Whether a session's detections can be confirmed, or why not. */
export type SendDetectionStatus = internalComponents['schemas']['SendDetectionStatus.schema'];
/** The limits every SEND upload is held to, each published as the one value the server enforces. */
export type SendUploadLimits = internalComponents['schemas']['SendUploadLimits.schema'];

/** One file's outcome within a {@link SendBundleValidationResult}. The UI paginates `findings` client-side. */
export type SendValidationResult = internalComponents['schemas']['SendValidationResult.schema'];
export type Finding = internalComponents['schemas']['SendFinding.schema'];
export type SendSuggestionOrigin = internalComponents['schemas']['SendSuggestionOrigin.schema'];
export type CrossDomainFinding = internalComponents['schemas']['SendCrossDomainFinding.schema'];
export type CrossDomainFindingGroup = internalComponents['schemas']['SendCrossDomainFindingGroup.schema'];
export type FindingsSummary = internalComponents['schemas']['SendFindingsSummary.schema'];
export type Severity = internalComponents['schemas']['SendSeverity.schema'];
export type FindingCategory = internalComponents['schemas']['SendFindingCategory.schema'];
export type SendRuleIdentity = internalComponents['schemas']['SendRuleIdentity.schema'];
export type SendRulePublisher = SendRuleIdentity['publisher'];
/** How serious a finding is, on five levels. Absent from findings stored before it existed; read `severity` then. */
export type SendSeverityLevel = internalComponents['schemas']['SendSeverityLevel.schema'];
/** Who assigned a finding's severity level: a published grading, our policy applied to CDISC's rule, or our own. */
export type SendSeveritySource = internalComponents['schemas']['SendSeveritySource.schema'];
/** Whether the FDA's own validator runs a check for the rule. Triage only, never a severity. */
export type SendRegulatorySalience = internalComponents['schemas']['SendRegulatorySalience.schema'];
/** The checks' findings at each level, safe to show on a severity chip. Absent from results stored before it existed. */
export type SendSeverityCounts = internalComponents['schemas']['SendSeverityCountsV1.schema'];
/** The FDA's text for the validator rules a session's result cites, with its credit. */
export type SendFdaValidatorRuleTexts = internalComponents['schemas']['SendFdaValidatorRuleTextsV1.schema'];

/**
 * Whether the run could validate the study at all, from its Trial Summary, and which terminology release
 * it checked against. Absent from results stored before scope was read, which is never to be read as in scope.
 */
export type SendRunScope = internalComponents['schemas']['SendRunScopeV1.schema'];
export type SendScopeState = internalComponents['schemas']['SendScopeState.schema'];
export type SendScopeReason = internalComponents['schemas']['SendScopeReason.schema'];
export type SendTrialSummaryDeclaration = internalComponents['schemas']['SendTrialSummaryDeclarationV1.schema'];
export type SendDeclarationStatus = internalComponents['schemas']['SendDeclarationStatus.schema'];
/** A pre-check related to the FDA's Technical Rejection Criteria: always a proxy, never blocking, never a finding. */
export type SendTrcProxy = internalComponents['schemas']['SendTrcProxyV1.schema'];
export type SendTrcProxyCheck = internalComponents['schemas']['SendTrcProxyCheck.schema'];
/** The activity phases that are the SEND validator's own, in the order a run takes them. */
export type SendValidatorPhase = internalComponents['schemas']['SendValidatorPhase.schema'];

/** A wire value: shown through the text `sendRuleOutcomes` gives it, never as-is. */
export type SendIncompleteReason = internalComponents['schemas']['SendIncompleteReason.schema'];

/** The whole result's header: exact counts, the rule tallies and outcomes, and every limit that applied. */
export type SendResultReport = internalComponents['schemas']['SendBoundedResultHeaderV1.schema'];
export type SendReportCounts = internalComponents['schemas']['SendReportSummaryV1.schema'];
export type SendAdvisoryCounts = internalComponents['schemas']['SendAdvisoryCountsV1.schema'];
export type SendResultTruncation = internalComponents['schemas']['SendResultTruncationV1.schema'];
export type SendResultLimitKind = internalComponents['schemas']['SendResultLimitKind.schema'];
export type SendCheckResult = internalComponents['schemas']['SendCheckResult.schema'];
export type SendRuleOutcome = internalComponents['schemas']['SendRuleOutcomeV1.schema'];
/** Which implementation produced a rule's outcome: CDISC's encoding, an audited in-house check, or our reading of the rule. */
export type SendEvaluatedBy = internalComponents['schemas']['SendEvaluatedBy.schema'];
export type SendCoverageGridRow = internalComponents['schemas']['SendCoverageGridRowV1.schema'];
/** Whether a rule's tier and reading are still ours, or a practitioner has reviewed them. */
export type SendReviewState = internalComponents['schemas']['SendReviewState.schema'];
/**
 * What this build can check, per population, beside what the run did: the published register, the encodings
 * outside it, and our own checks. Never summed. Absent from results stored before it existed.
 */
export type SendCapabilityReport = internalComponents['schemas']['SendCapabilityReportV1.schema'];
/** The cross-domain checks' own counts, as the server publishes them. Absent from results stored before it. */
export type SendCrossDomainTier = internalComponents['schemas']['SendCrossDomainTierV1.schema'];
/** Java's verdict on whether the run, each part and each dataset passed exactly. Absent from older results. */
export type SendExactPass = internalComponents['schemas']['SendExactPassV1.schema'];

/**
 * How many rules landed in each reporting bucket, counted by the server. Read as given: the buckets'
 * predicates depend on each other, so recounting them here would get them wrong.
 */
export type SendVerdictTally = internalComponents['schemas']['SendVerdictTallyV1.schema'];

/**
 * Whether Benchling In Vivo AI took part in a run. Names no model, by design: the model behind it
 * can change without the customer seeing it.
 */
export type SendRunMetadata = internalComponents['schemas']['SendRunMetadataV1.schema'];
export type SendReviewCoverage = internalComponents['schemas']['SendReviewCoverageV1.schema'];
/** Whether Benchling In Vivo AI's review of a run completed. Absent on results stored before it was recorded. */
export type SendEnrichmentStatus = internalComponents['schemas']['SendEnrichmentStatus.schema'];

export interface StreamingText {
  executionApiId: AgentExecutionApiId;
  accumulatedText: string;
  isStreaming: boolean;
}
